import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Badge from "@/components/base/Badge";
import ConfirmDeleteModal from "@/components/base/ConfirmDeleteModal";
import EmptyState from "@/components/base/EmptyState";
import { cn } from "@/utils/cn";
import { formatDate, formatFcfa } from "@/utils/format";
import { usePlatformData } from "@/store/platform/PlatformDataProvider";
import { PLATFORM_SCOPE_LABELS } from "@/store/platform/types";
import PlatformImmeubleForm from "@/pages/super-admin/components/PlatformImmeubleForm";
import { BIEN_STATUT_LABELS, BIEN_TYPE_LABELS, type BienStatut, type BienType } from "@/pages/proprietaire/biens/types";

export default function SuperAdminImmeubleDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getImmeuble, biensByImmeuble, contratsByImmeuble, removeImmeuble } = usePlatformData();

  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const immeuble = id ? getImmeuble(id) : undefined;

  if (!immeuble) {
    return (
      <div className="flex flex-col gap-6">
        <Link
          to="/super-admin/immeubles"
          className="inline-flex w-fit items-center gap-2 whitespace-nowrap text-sm font-label font-medium text-foreground-600 transition-colors hover:text-foreground-950 cursor-pointer"
        >
          <i className="ri-arrow-left-line text-base" aria-hidden="true" />
          Retour aux immeubles
        </Link>
        <EmptyState
          icon="ri-building-2-line"
          title="Immeuble introuvable"
          description="Cet immeuble n'existe pas ou n'est plus disponible."
          action={
            <Link
              to="/super-admin/immeubles"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-arrow-left-line text-base" aria-hidden="true" />
              Voir tous les immeubles
            </Link>
          }
        />
      </div>
    );
  }

  const biens = biensByImmeuble(immeuble.id);
  const contrats = contratsByImmeuble(immeuble.id);

  const infos: { label: string; value: string; icon: string }[] = [
    { label: "Espace", value: PLATFORM_SCOPE_LABELS[immeuble.scope], icon: "ri-layout-grid-line" },
    { label: "Région", value: immeuble.region || "—", icon: "ri-map-2-line" },
    { label: "Province", value: immeuble.province || "—", icon: "ri-community-line" },
    { label: "Commune / Ville", value: immeuble.commune || "—", icon: "ri-map-pin-2-line" },
    { label: "Secteur / Quartier", value: immeuble.secteur || "—", icon: "ri-road-map-line" },
    { label: "Adresse", value: immeuble.adresse || "—", icon: "ri-navigation-line" },
    { label: "Nombre de niveaux", value: String(immeuble.niveaux), icon: "ri-stack-line" },
    { label: "Créé le", value: formatDate(immeuble.createdAt), icon: "ri-calendar-line" },
  ];

  const confirmDelete = () => {
    if (biens.length > 0) return;
    removeImmeuble(immeuble.id);
    setDeleteOpen(false);
    navigate("/super-admin/immeubles");
  };

  return (
    <div className="flex flex-col gap-6">
      <Link
        to="/super-admin/immeubles"
        className="inline-flex w-fit items-center gap-2 whitespace-nowrap text-sm font-label font-medium text-foreground-600 transition-colors hover:text-foreground-950 cursor-pointer"
      >
        <i className="ri-arrow-left-line text-base" aria-hidden="true" />
        Retour aux immeubles
      </Link>

      <section className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 md:p-6 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex min-w-0 items-start gap-4">
          {immeuble.photo ? (
            <img
              src={immeuble.photo}
              alt={immeuble.nom}
              className="h-16 w-16 shrink-0 rounded-lg object-cover object-top"
            />
          ) : (
            <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
              <i className="ri-building-2-line text-2xl" aria-hidden="true" />
            </span>
          )}
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone={immeuble.scope === "agence" ? "accent" : "primary"}>
                {PLATFORM_SCOPE_LABELS[immeuble.scope]}
              </Badge>
              <Badge tone="neutral" icon="ri-home-4-line">
                {biens.length} bien{biens.length > 1 ? "s" : ""}
              </Badge>
              <Badge tone="neutral" icon="ri-file-text-line">
                {contrats.length} contrat{contrats.length > 1 ? "s" : ""}
              </Badge>
            </div>
            <h2 className="mt-3 font-heading text-xl font-semibold text-foreground-950 md:text-2xl">{immeuble.nom}</h2>
            <p className="mt-1 text-sm text-foreground-600">
              {immeuble.commune}, {immeuble.province} · {immeuble.region}
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row lg:shrink-0">
          <button
            type="button"
            onClick={() => setEditOpen(true)}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-edit-line text-base" aria-hidden="true" />
            Modifier
          </button>
          <button
            type="button"
            onClick={() => setDeleteOpen(true)}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-primary-300 bg-background-50 px-4 py-2.5 text-sm font-label font-medium text-primary-700 transition-colors hover:bg-primary-50 cursor-pointer"
          >
            <i className="ri-delete-bin-line text-base" aria-hidden="true" />
            Supprimer
          </button>
        </div>
      </section>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <section className="flex flex-col rounded-lg border border-background-200 bg-background-50 lg:col-span-2">
          <header className="flex items-center gap-2.5 border-b border-background-200 px-4 py-3.5 md:px-5">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-secondary-50 text-secondary-600">
              <i className="ri-information-line text-base" aria-hidden="true" />
            </span>
            <h3 className="font-heading text-sm font-semibold text-foreground-950 md:text-base">
              Informations de l'immeuble
            </h3>
          </header>
          <div className="p-4 md:p-5">
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {infos.map((info) => (
                <div key={info.label} className="flex items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-background-100 text-foreground-500">
                    <i className={cn(info.icon, "text-base")} aria-hidden="true" />
                  </span>
                  <div className="min-w-0">
                    <dt className="text-xs text-foreground-500">{info.label}</dt>
                    <dd className="truncate text-sm font-label font-medium text-foreground-950">{info.value}</dd>
                  </div>
                </div>
              ))}
            </dl>
            {(immeuble.description || immeuble.infosComplementaires) && (
              <div className="mt-5 flex flex-col gap-4 border-t border-background-200 pt-4">
                {immeuble.description && (
                  <div>
                    <p className="text-xs font-label font-semibold uppercase tracking-wide text-foreground-400">
                      Description
                    </p>
                    <p className="mt-1.5 text-sm leading-relaxed text-foreground-700">{immeuble.description}</p>
                  </div>
                )}
                {immeuble.infosComplementaires && (
                  <div>
                    <p className="text-xs font-label font-semibold uppercase tracking-wide text-foreground-400">
                      Informations complémentaires
                    </p>
                    <p className="mt-1.5 text-sm leading-relaxed text-foreground-700">
                      {immeuble.infosComplementaires}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>

        <section className="flex flex-col rounded-lg border border-background-200 bg-background-50">
          <header className="flex items-center gap-2.5 border-b border-background-200 px-4 py-3.5 md:px-5">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-accent-50 text-accent-700">
              <i className="ri-home-4-line text-base" aria-hidden="true" />
            </span>
            <h3 className="font-heading text-sm font-semibold text-foreground-950 md:text-base">Biens rattachés</h3>
          </header>
          <div className="p-4 md:p-5">
            {biens.length === 0 ? (
              <div className="flex flex-col items-center justify-center rounded-md bg-background-100/70 px-6 py-8 text-center">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-100 text-accent-700">
                  <i className="ri-home-4-line text-xl" aria-hidden="true" />
                </span>
                <p className="mt-3 text-sm font-heading font-semibold text-foreground-950">Aucun bien</p>
                <p className="mt-1 text-xs text-foreground-600">
                  Les biens de cet immeuble apparaîtront ici dès leur création.
                </p>
              </div>
            ) : (
              <ul className="flex flex-col divide-y divide-background-200">
                {biens.map((bien) => (
                  <li key={bien.id} className="flex items-center justify-between gap-3 py-3 first:pt-0 last:pb-0">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-label font-medium text-foreground-950">
                        {bien.reference || BIEN_TYPE_LABELS[bien.type as BienType]}
                      </p>
                      <p className="mt-0.5 truncate text-xs text-foreground-500">
                        {BIEN_TYPE_LABELS[bien.type as BienType]} · {BIEN_STATUT_LABELS[bien.statut as BienStatut]}
                      </p>
                    </div>
                    <span className="shrink-0 text-xs font-label font-medium text-foreground-700">
                      {formatFcfa(bien.loyerMensuel)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      </div>

      <PlatformImmeubleForm open={editOpen} onClose={() => setEditOpen(false)} immeuble={immeuble} />

      <ConfirmDeleteModal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={confirmDelete}
        description={`Supprimer l'immeuble « ${immeuble.nom} » ? Cette action est définitive.`}
        blockedMessage={
          biens.length > 0
            ? `Cet immeuble possède ${biens.length} bien${biens.length > 1 ? "s" : ""} rattaché${
                biens.length > 1 ? "s" : ""
              }. Supprimez ou déplacez d'abord ces biens.`
            : undefined
        }
      />
    </div>
  );
}